import { Image, Text, View } from "@react-pdf/renderer";
import { useMemo } from "react";

import { format, parse } from "date-fns";

import {
  CLINIC_LOGO_MAX_HEIGHT,
  CLINIC_LOGO_MAX_WIDTH
} from "components/print-header/print-header-report/clinic-logo-constants";
import { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { Physician } from "services/types/physician.type";
import { MinimalStaffMember } from "services/types/staff.type";
import {
  formatPhoneNumber,
  formatResourceUrl,
  getAgeInMonthsOrYears,
  getFullName
} from "utils/common";
import { getPhysiciansAsText } from "utils/physicians.utils";

import PdfDocumentLabelValue from "../components/pdf-document-label-value.component";
import { usePdfChromeScale } from "../pdf-page-chrome";

type PdfDocumentHeaderReportProps = {
  clinic?: Clinic;
  staffMember?: MinimalStaffMember;
  patient?: Patient;
  dateOfVisit?: string | number | Date;
  dateOfVisitLabel?: string;
  showDateOfVisit?: boolean;
  physicians?: Array<Physician | undefined>;
  compact?: boolean;
  /**
   * Render the practice logo block. Defaults to `true`, so every existing caller is unchanged.
   *
   * Exists for a surface that offers "practice logo" and "patient header" as INDEPENDENT choices
   * (the After Visit Summary print dialog). This header always drew the logo, so without an opt-out
   * a "practice logo" toggle is a dead control whenever the patient header is on. Hiding it drops
   * the 30% column entirely rather than leaving a gap — the row is `flexWrap`, so the practice and
   * patient blocks simply shift left.
   */
  showLogo?: boolean;
};
const DATE_MASK = "MM/dd/yyyy";
export default function PdfDocumentHeaderReport({
  clinic,
  staffMember,
  patient,
  dateOfVisit,
  dateOfVisitLabel = "Date of Visit:",
  showDateOfVisit = true,
  physicians,
  compact = false,
  showLogo = true
}: PdfDocumentHeaderReportProps) {
  const staffMemberName = staffMember
    ? getFullName({
        firstName: staffMember.first_name || "",
        lastName: staffMember.last_name || "",
        title: staffMember.title,
        suffix: staffMember.suffix
      })
    : "";

  const address_line1 = [clinic?.street_address_1, clinic?.street_address_2]
    .filter(Boolean)
    .join(", ");

  const address_line2 = [clinic?.city, clinic?.state, clinic?.zip_code]
    .filter(Boolean)
    .join(", ");

  const clinicPhone =
    formatPhoneNumber(clinic?.phone || "", "parenthesis") || "";

  const patientPhone =
    formatPhoneNumber(
      patient?.phone || patient?.secondary_phone || "",
      "parenthesis"
    ) || "";

  const patientName = patient
    ? getFullName({
        firstName: patient.first_name ?? "",
        lastName: patient.last_name ?? "",
        suffix: patient.suffix
      })
    : "";

  const patientSex = patient?.sex ?? "";

  const patientDateOfBirth = patient?.birthdate
    ? format(
        typeof patient?.birthdate === "string"
          ? parse(patient?.birthdate, "yyyy-MM-dd", new Date())
          : new Date(patient?.birthdate),
        DATE_MASK
      )
    : "";

  const patientDateOfVisit =
    dateOfVisit && showDateOfVisit
      ? format(new Date(dateOfVisit), DATE_MASK)
      : "";

  const physiciansText = getPhysiciansAsText(physicians);
  // ENG-2277: lengths are a legacy page's px, scaled on a Form's page (see pdf-page-chrome).
  const { px, scale } = usePdfChromeScale();
  const labelWidth = px(75);
  const logo = clinic?.logo || clinic?.practice?.logo;
  const logoSrc = useMemo(() => formatResourceUrl(logo, true), [logo]);

  return (
    <View wrap={false} style={{ width: "100%" }}>
      <View
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: px(compact ? 2 : 8)
        }}
      >
        <View
          style={{
            width: "100%",
            height: px(3),
            backgroundColor: "#381337",
            marginBottom: px(compact ? 1 : 4)
          }}
        ></View>
        {showLogo && (
          <View
            style={{
              width: "30%"
            }}
          >
            <View
              style={{
                height: parseFloat(CLINIC_LOGO_MAX_HEIGHT) * 0.75 * scale,
                width: parseFloat(CLINIC_LOGO_MAX_WIDTH) * 0.75 * scale
              }}
            >
              {Boolean(logo) && (
                <Image
                  style={{
                    objectFit: "scale-down",
                    marginBottom: "0px"
                  }}
                  src={logoSrc}
                  cache={false}
                />
              )}
            </View>
          </View>
        )}

        <View
          style={{
            width: px(360)
          }}
        >
          <PdfDocumentLabelValue
            compact={compact}
            labelWidth={labelWidth}
            label="Practice name:"
            value={clinic?.display_name ?? clinic?.practice?.name ?? ""}
            style={{ paddingTop: "0px" }}
          />
          <PdfDocumentLabelValue
            compact={compact}
            labelWidth={labelWidth}
            label="Provider:"
            value={staffMemberName}
          />
          <PdfDocumentLabelValue
            compact={compact}
            labelWidth={labelWidth}
            label="Address:"
            value={address_line1}
          />
          <PdfDocumentLabelValue
            compact={compact}
            labelWidth={labelWidth}
            label="&nbsp;"
            value={address_line2}
          />
          <PdfDocumentLabelValue
            compact={compact}
            labelWidth={labelWidth}
            label="Phone:"
            value={clinicPhone}
            noBorder
          />
        </View>

        <View
          style={{
            width: px(294)
          }}
        >
          <PdfDocumentLabelValue
            compact={compact}
            label="Name:"
            value={patientName}
            style={{ paddingTop: "0px" }}
          />

          <PdfDocumentLabelValue
            compact={compact}
            label="Sex:"
            value={
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: px(14)
                }}
              >
                <Text style={{ fontSize: px(14), color: "#000000" }}>
                  {patientSex}
                </Text>
                <View style={{ paddingTop: px(2) }}>
                  <Text style={{ fontSize: px(11), color: "#000000" }}>
                    Age:
                  </Text>
                </View>
                <Text style={{ fontSize: px(14), color: "#000000" }}>
                  {getAgeInMonthsOrYears(
                    patient?.age_years ?? "",
                    patient?.birthdate,
                    dateOfVisit
                  )}
                </Text>

                <View style={{ paddingTop: px(2) }}>
                  <Text style={{ fontSize: px(11), color: "#000000" }}>
                    DOB:
                  </Text>
                </View>
                <Text style={{ fontSize: px(14), color: "#000000" }}>
                  {patientDateOfBirth}
                </Text>
              </View>
            }
          />

          {showDateOfVisit && (
            <PdfDocumentLabelValue
              compact={compact}
              label={dateOfVisitLabel}
              value={patientDateOfVisit}
            />
          )}
          <PdfDocumentLabelValue
            compact={compact}
            label="Phone:"
            value={patientPhone}
          />
          {/* Boolean(), not a bare string: getPhysiciansAsText returns "" when there are
              no physicians, and React renders that empty string as a child of this View.
              react-pdf has no text node to put it in and logs "Invalid '' string child
              outside <Text> component" on every generation (SN-19144). */}
          {Boolean(physiciansText) && (
            <PdfDocumentLabelValue
              compact={compact}
              label="Physicians"
              value={physiciansText}
              noBorder
            />
          )}
        </View>
        <View
          style={{
            width: "100%",
            height: px(3),
            backgroundColor: "#381337",
            marginTop: px(compact ? 1 : 4),
            marginBottom: px(compact ? 2 : 5)
          }}
        ></View>
      </View>
    </View>
  );
}
